
import React, { useState, useEffect, useRef } from 'react';
import { cn } from "@/lib/utils";
import { RefreshCw } from 'lucide-react';
import { checkForUpdates } from '@/components/ui/UpdateNotification';
import { toast } from 'sonner';

interface PullToRefreshProps {
  children: React.ReactNode;
  onRefresh?: () => Promise<void>;
  className?: string;
}

export function PullToRefresh({
  children,
  onRefresh,
  className
}: PullToRefreshProps) {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const THRESHOLD = 80; // Distance in pixels needed to trigger refresh

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e: TouchEvent) => {
      // Only allow pull to refresh at the top of the page
      if (window.scrollY <= 0) {
        startY.current = e.touches[0].clientY;
        setIsPulling(true);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPulling) return;
      
      const currentY = e.touches[0].clientY;
      const distance = currentY - startY.current;
      
      // Only allow pulling down, not up
      if (distance > 0) {
        // Apply resistance to make the pull feel natural
        const newDistance = Math.min(distance * 0.5, THRESHOLD * 1.5);
        setPullDistance(newDistance);
        
        // Prevent default scrolling behavior when pulling
        if (window.scrollY <= 0) {
          e.preventDefault();
        }
      }
    };

    const handleTouchEnd = async () => {
      if (!isPulling) return;
      
      if (pullDistance >= THRESHOLD) {
        // Trigger refresh
        setIsRefreshing(true);
        
        try {
          // Check for updates first
          await checkForUpdates();
          
          // If custom refresh logic is provided, execute it
          if (onRefresh) {
            await onRefresh();
          }
          
          toast("Refreshed", {
            description: "Application has been refreshed with the latest version.",
            className: "bg-white border border-purple-100"
          });
        } catch (error) {
          
          toast("Refresh failed", {
            description: "Could not check for updates. Please try again.",
            className: "bg-white border border-red-100"
          });
        } finally {
          setIsRefreshing(false);
          setPullDistance(0);
          setIsPulling(false);
        }
      } else {
        // Not enough to trigger, reset
        setPullDistance(0);
        setIsPulling(false);
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isPulling, pullDistance, onRefresh]);

  // Calculate rotate animation based on pull distance
  const rotateAmount = Math.min(pullDistance / THRESHOLD * 360, 360);
  
  // Set CSS custom properties for pull distance and rotation
  useEffect(() => {
    if (containerRef.current) {
      // Format values as plain strings instead of template literals to avoid CSS minify warnings
      containerRef.current.style.setProperty('--pull-distance', Math.floor(pullDistance) + 'px');
      containerRef.current.style.setProperty('--rotate-amount', rotateAmount + 'deg');
    }
  }, [pullDistance, rotateAmount]);
  
  return (
    <div ref={containerRef} className={cn("relative overflow-hidden", className)}>
      <div 
        className={cn(
          "absolute top-0 left-0 right-0 flex items-center justify-center",
          "transition-transform duration-300",
          isRefreshing ? "translate-y-0" : (pullDistance > 0 ? "translate-y-[var(--pull-distance)]" : "-translate-y-full")
        )}
        style={{ 
          transform: isRefreshing 
            ? 'translateY(16px)' 
            : pullDistance > 0 
              ? 'translateY(var(--pull-distance))' 
              : 'translateY(-100%)'
        }}
      >
        <div className="flex flex-col items-center gap-2 text-primary">
          <RefreshCw 
            className={cn(
              "transition-all",
              isRefreshing ? "animate-spin" : "",
            )}
            style={{ 
              transform: !isRefreshing ? 'rotate(var(--rotate-amount))' : 'rotate(0deg)',
            }}
            size={24}
          />
          <span className="text-sm font-medium">
            {isRefreshing ? "Refreshing..." : pullDistance >= THRESHOLD ? "Release to refresh" : "Pull to refresh"}
          </span>
        </div>
      </div>
      
      {/* Apply transition to children when pulling */}
      <div 
        className="transition-transform duration-300"
        style={{ 
          transform: pullDistance > 0 ? 'translateY(var(--pull-distance))' : 'translateY(0)' 
        }}
      >
        {children}
      </div>
    </div>
  );
}
